import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Camera, Car, ClipboardCheck, AlertTriangle, RefreshCw, Search,
  ArrowRight, Check, X, Upload,
} from "lucide-react";

const FUEL = ["empty", "1/4", "1/2", "3/4", "full"];
const CLEAN = ["clean", "acceptable", "dirty"];

function StatusPill({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] label-caps ${
        ok ? "bg-lime-400/15 text-lime-200 ring-1 ring-lime-400/40"
           : "bg-white/8 text-white/50 ring-1 ring-white/15"
      }`}
    >
      {ok ? <Check size={10} /> : <X size={10} />} {label}
    </span>
  );
}

function PhotoGrid({ files, onRemove }) {
  if (!files?.length) return null;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
      {files.map((f) => (
        <div key={f.file_id} className="relative group rounded-lg overflow-hidden border border-white/10">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${f.url}`}
            alt=""
            loading="lazy"
            className="w-full h-24 object-cover"
          />
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(f.file_id)}
              className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
              aria-label="Remove photo"
            >
              <X size={10} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function InspectionForm({ kind, booking, existing, onSaved, onCancel }) {
  const [odometer, setOdometer] = useState(existing?.odometer ?? "");
  const [fuel, setFuel] = useState(existing?.fuel_level ?? "");
  const [cleanliness, setCleanliness] = useState(existing?.cleanliness ?? "clean");
  const [keys, setKeys] = useState(existing?.keys_given ?? true);
  const [notes, setNotes] = useState(existing?.staff_notes ?? "");
  const [damage, setDamage] = useState(existing?.damage_notes ?? "");
  const [extra, setExtra] = useState(existing?.additional_charges ?? "");
  const [ack, setAck] = useState(existing?.customer_acknowledged ?? false);
  const [initials, setInitials] = useState(existing?.staff_signature ?? "");
  const [exterior, setExterior] = useState(existing?.exterior_photo_ids || []);
  const [interior, setInterior] = useState(existing?.interior_photo_ids || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadTo = async (setter, files) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/inspection/upload-photo", fd);
        setter((prev) => [...prev, { file_id: data.file_id, url: data.url }]);
      }
    } catch (e) {
      toast.error("Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!odometer) return toast.error("Odometer reading required");
    setSaving(true);
    try {
      const payload = {
        kind,
        odometer: Number(odometer),
        fuel_level: fuel || null,
        cleanliness,
        keys_given: !!keys,
        exterior_photo_ids: exterior.map((f) => f.file_id ?? f),
        interior_photo_ids: interior.map((f) => f.file_id ?? f),
        damage_notes: damage.trim() || null,
        damage_markers: existing?.damage_markers || [],
        additional_charges: extra ? Number(extra) : null,
        staff_notes: notes.trim() || null,
        customer_acknowledged: ack,
        staff_signature: initials.trim() || null,
      };
      const { data } = await api.post(`/admin/bookings/${booking.id}/inspection`, payload);
      toast.success(`${kind === "checkout" ? "Check-out" : "Return"} inspection saved · status → ${data.new_status}`);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save inspection");
    } finally {
      setSaving(false);
    }
  };

  const label = kind === "checkout" ? "Check-Out Inspection" : "Return Inspection";
  const accent = kind === "checkout" ? "text-cyan" : "text-lime-300";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className={`label-caps text-[10px] ${accent}`}>{label}</div>
          <div className="text-white font-serif text-lg">{booking.vehicle_name}</div>
          <div className="text-white/50 text-xs">
            {booking.full_name} · {booking.reservation_id || booking.id}
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-white/50 hover:text-white text-xs"
          data-testid="inspection-cancel"
        >
          Close
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <div className="label-caps text-[9px] text-white/50 mb-1">Odometer</div>
          <input
            data-testid="inspection-odometer"
            type="number"
            value={odometer}
            onChange={(e) => setOdometer(e.target.value)}
            placeholder="e.g. 42150"
            className="w-full bg-ink-800 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan"
          />
        </label>
        <label className="block">
          <div className="label-caps text-[9px] text-white/50 mb-1">Fuel level</div>
          <div className="flex gap-1" data-testid="inspection-fuel">
            {FUEL.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFuel(f)}
                className={`flex-1 px-2 py-2 rounded-lg text-xs transition ${
                  fuel === f ? "bg-cyan/20 text-cyan ring-1 ring-cyan/50" : "bg-white/5 text-white/60 hover:bg-white/10"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </label>
        <label className="block">
          <div className="label-caps text-[9px] text-white/50 mb-1">Cleanliness</div>
          <div className="flex gap-1" data-testid="inspection-cleanliness">
            {CLEAN.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCleanliness(c)}
                className={`flex-1 px-2 py-2 rounded-lg text-xs transition capitalize ${
                  cleanliness === c ? "bg-cyan/20 text-cyan ring-1 ring-cyan/50" : "bg-white/5 text-white/60 hover:bg-white/10"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </label>
        <label className="flex items-center gap-2 text-sm text-white/80 mt-6">
          <input
            type="checkbox"
            checked={keys}
            onChange={(e) => setKeys(e.target.checked)}
            data-testid="inspection-keys"
            className="w-4 h-4 accent-cyan"
          />
          Keys handed over
        </label>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="label-caps text-[9px] text-white/50">Exterior photos</div>
          <label className="cursor-pointer text-cyan text-xs inline-flex items-center gap-1">
            <Upload size={12} /> Add
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid="inspection-exterior-upload"
              onChange={(e) => uploadTo(setExterior, e.target.files)}
            />
          </label>
        </div>
        <PhotoGrid files={exterior} onRemove={(id) => setExterior((p) => p.filter((f) => f.file_id !== id))} />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="label-caps text-[9px] text-white/50">Interior photos</div>
          <label className="cursor-pointer text-cyan text-xs inline-flex items-center gap-1">
            <Upload size={12} /> Add
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid="inspection-interior-upload"
              onChange={(e) => uploadTo(setInterior, e.target.files)}
            />
          </label>
        </div>
        <PhotoGrid files={interior} onRemove={(id) => setInterior((p) => p.filter((f) => f.file_id !== id))} />
      </div>

      <label className="block">
        <div className="label-caps text-[9px] text-white/50 mb-1">Damage notes</div>
        <textarea
          data-testid="inspection-damage"
          rows={3}
          value={damage}
          onChange={(e) => setDamage(e.target.value)}
          placeholder={kind === "checkout" ? "Pre-existing scratches, dings, wear…" : "New damage discovered on return"}
          className="w-full bg-ink-800 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan"
        />
      </label>

      {kind === "return" && (
        <label className="block">
          <div className="label-caps text-[9px] text-white/50 mb-1">Additional charges ($)</div>
          <input
            type="number"
            data-testid="inspection-extra-charges"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="Cleaning fee, late fee, damage…"
            className="w-full bg-ink-800 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan"
          />
        </label>
      )}

      <label className="block">
        <div className="label-caps text-[9px] text-white/50 mb-1">Staff notes</div>
        <textarea
          data-testid="inspection-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full bg-ink-800 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan"
        />
      </label>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="flex items-center gap-2 text-sm text-white/80">
          <input
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            data-testid="inspection-ack"
            className="w-4 h-4 accent-cyan"
          />
          Customer acknowledged in person
        </label>
        <label className="block">
          <div className="label-caps text-[9px] text-white/50 mb-1">Staff initials</div>
          <input
            data-testid="inspection-initials"
            value={initials}
            onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 4))}
            placeholder="e.g. JR"
            className="w-full bg-ink-800 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan"
          />
        </label>
      </div>

      <div className="pt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={saving || uploading}
          data-testid="inspection-submit"
          className={`px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 ${
            kind === "checkout" ? "bg-cyan text-black" : "bg-lime-400 text-black"
          }`}
        >
          {saving ? "Saving…" : uploading ? "Uploading…" : (
            <>
              {kind === "checkout" ? "Save Check-Out & Mark In Progress" : "Save Return & Complete Rental"}
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function CompareView({ checkoutIns, returnIns }) {
  if (!checkoutIns && !returnIns) return null;
  const rows = [
    ["Odometer", checkoutIns?.odometer, returnIns?.odometer],
    ["Fuel", checkoutIns?.fuel_level, returnIns?.fuel_level],
    ["Cleanliness", checkoutIns?.cleanliness, returnIns?.cleanliness],
    ["Keys handed over", checkoutIns?.keys_given ? "Yes" : "No", returnIns?.keys_given ? "Yes" : "No"],
    ["Damage notes", checkoutIns?.damage_notes || "—", returnIns?.damage_notes || "—"],
    ["Additional charges", "—", returnIns?.additional_charges ? `$${returnIns.additional_charges}` : "—"],
  ];
  const beforeExt = (checkoutIns?.exterior_photo_ids || []).map((id) => ({ file_id: id, url: `/api/admin/inspection/photo/${id}` }));
  const beforeInt = (checkoutIns?.interior_photo_ids || []).map((id) => ({ file_id: id, url: `/api/admin/inspection/photo/${id}` }));
  const afterExt = (returnIns?.exterior_photo_ids || []).map((id) => ({ file_id: id, url: `/api/admin/inspection/photo/${id}` }));
  const afterInt = (returnIns?.interior_photo_ids || []).map((id) => ({ file_id: id, url: `/api/admin/inspection/photo/${id}` }));
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-cyan/25 bg-cyan/[0.04] p-4">
          <div className="label-caps text-[9px] text-cyan mb-2">Before · check-out</div>
          <PhotoGrid files={[...beforeExt, ...beforeInt]} />
          {!checkoutIns && <div className="text-white/40 text-xs">No check-out inspection on file.</div>}
        </div>
        <div className="rounded-2xl border border-lime-400/25 bg-lime-400/[0.04] p-4">
          <div className="label-caps text-[9px] text-lime-300 mb-2">After · return</div>
          <PhotoGrid files={[...afterExt, ...afterInt]} />
          {!returnIns && <div className="text-white/40 text-xs">Return inspection pending.</div>}
        </div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="text-white/50 label-caps text-[10px] bg-white/[0.03]">
            <tr>
              <th className="text-left px-3 py-2">Field</th>
              <th className="text-left px-3 py-2 text-cyan">Check-out</th>
              <th className="text-left px-3 py-2 text-lime-300">Return</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([k, a, b]) => (
              <tr key={k} className="border-t border-white/8">
                <td className="px-3 py-2 text-white/60">{k}</td>
                <td className="px-3 py-2 text-white">{a || "—"}</td>
                <td className="px-3 py-2 text-white">{b || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function InspectionsTab() {
  const [overdue, setOverdue] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [insData, setInsData] = useState(null);
  const [mode, setMode] = useState("view"); // view | checkout | return
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [ov, bk] = await Promise.all([
        api.get("/admin/bookings/overdue"),
        api.get("/admin/bookings"),
      ]);
      setOverdue(ov.data || []);
      setAllBookings(bk.data || []);
    } catch (e) {
      toast.error("Couldn't load rentals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rentals = useMemo(
    () => allBookings.filter((b) => b.type === "rental"),
    [allBookings]
  );

  const bySection = useMemo(() => {
    const now = new Date();
    return {
      needsCheckout: rentals.filter((b) =>
        (b.status === "confirmed" || b.status === "pending_payment") && !b.checkout_inspection),
      inProgress: rentals.filter((b) => b.status === "in_progress"),
      needsReturn: rentals.filter((b) => b.status === "in_progress" && !b.return_inspection),
      completed: rentals.filter((b) => b.status === "completed" && b.return_inspection).slice(0, 20),
      overdue,
    };
  }, [rentals, overdue]);

  const filterFn = (b) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [b.full_name, b.vehicle_name, b.reservation_id, b.id, b.customer_phone]
      .filter(Boolean).some((f) => f.toLowerCase().includes(s));
  };

  const openBooking = async (b) => {
    setSelected(b);
    setMode("view");
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/inspection`);
      setInsData(data);
    } catch (e) {
      setInsData(null);
    }
  };

  const Section = ({ title, rows, tone = "white" }) => (
    <div className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
        <div className={`label-caps text-[10px] tracking-widest text-${tone}`}>
          {title} <span className="text-white/40">· {rows.length}</span>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-white/40 text-xs">Nothing here yet.</div>
      ) : (
        <div className="divide-y divide-white/8">
          {rows.filter(filterFn).map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => openBooking(b)}
              data-testid={`inspection-row-${b.id}`}
              className="w-full text-left px-4 py-3 hover:bg-white/[0.03] transition flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm truncate">
                  {b.vehicle_name} · <span className="text-white/60">{b.full_name || "—"}</span>
                </div>
                <div className="text-white/40 text-[11px]">
                  {b.reservation_id || b.id} · {(b.start_at || "").slice(0, 16).replace("T", " ")} → {(b.end_at || "").slice(0, 16).replace("T", " ")}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <StatusPill ok={!!b.checkout_inspection} label="Check-out" />
                <StatusPill ok={!!b.return_inspection} label="Return" />
                {typeof b.hours_overdue === "number" && (
                  <span className="text-[10px] text-red-300 label-caps">
                    {b.hours_overdue}h overdue
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="bo-inspections-root" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps text-cyan text-[11px] mb-1">Fleet operations</div>
          <h2 className="font-serif text-2xl text-white flex items-center gap-2">
            <ClipboardCheck className="text-cyan" size={20} /> Vehicle Inspections
          </h2>
          <p className="text-white/50 text-xs mt-1 max-w-2xl">
            Digital check-out and return inspections for every rental. Photos, odometer, fuel,
            cleanliness, damage — captured on device and tied to the booking's audit trail.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-ink-800 border border-white/10 rounded-full px-3 py-2">
            <Search size={13} className="text-white/40" />
            <input
              data-testid="bo-inspections-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer, vehicle, reservation…"
              className="bg-transparent px-2 text-xs outline-none text-white/85 w-56"
            />
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="bo-inspections-refresh"
            className="px-3 py-2 rounded-full border border-white/15 text-xs label-caps text-white/70 hover:text-white hover:border-white/30 inline-flex items-center gap-1.5"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 text-sm text-center py-12">Loading rentals…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <Section title="⚠︎ Overdue" rows={bySection.overdue} tone="red-300" />
          <Section title="Needs Check-Out" rows={bySection.needsCheckout} tone="cyan" />
          <Section title="Needs Return" rows={bySection.needsReturn} tone="lime-300" />
          <Section title="Recently Completed" rows={bySection.completed} tone="white/60" />
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-end"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-ink-900 border-l border-white/10 w-full sm:max-w-[720px] h-full overflow-y-auto p-6 space-y-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="inspection-drawer"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="label-caps text-cyan text-[10px]">Rental</div>
                <div className="font-serif text-xl text-white">
                  {selected.vehicle_name}
                </div>
                <div className="text-white/50 text-xs">
                  {selected.reservation_id || selected.id} · {selected.full_name}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-white/60 hover:text-white text-xs"
              >
                Close
              </button>
            </div>

            {mode === "view" && (
              <div className="space-y-4">
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    data-testid="inspection-start-checkout"
                    onClick={() => setMode("checkout")}
                    className="px-4 py-2 rounded-full bg-cyan/20 text-cyan ring-1 ring-cyan/50 text-xs label-caps inline-flex items-center gap-1.5"
                  >
                    <Camera size={12} /> {insData?.checkout ? "Edit Check-Out" : "Start Check-Out"}
                  </button>
                  <button
                    type="button"
                    data-testid="inspection-start-return"
                    onClick={() => setMode("return")}
                    disabled={!insData?.checkout}
                    className="px-4 py-2 rounded-full bg-lime-400/20 text-lime-300 ring-1 ring-lime-400/50 text-xs label-caps inline-flex items-center gap-1.5 disabled:opacity-40"
                    title={!insData?.checkout ? "Check-out required first" : ""}
                  >
                    <Car size={12} /> {insData?.return ? "Edit Return" : "Start Return"}
                  </button>
                </div>
                <CompareView checkoutIns={insData?.checkout} returnIns={insData?.return} />
              </div>
            )}

            {(mode === "checkout" || mode === "return") && (
              <InspectionForm
                kind={mode}
                booking={selected}
                existing={mode === "checkout" ? insData?.checkout : insData?.return}
                onSaved={(res) => {
                  toast.success("Inspection saved");
                  load();
                  openBooking({ ...selected, status: res.new_status });
                }}
                onCancel={() => setMode("view")}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
